/**
 * Which applications a funder can actually act on.
 *
 * The marketplace is open — every approved application is visible to every
 * funder — so "approved" is a marketplace-wide number, not a personal one.
 * What a funder wants to know on their dashboard is narrower: how many
 * applications are still waiting for an offer *from them*.
 *
 * Both halves of that answer already ride along on GET /applications/available,
 * which annotates each row with this funder's `funder_has_offer` and
 * `funder_not_interested`. The Settings half is applied in the browser because
 * that is where the funder's saved preferences live. Deriving the count from
 * the same array Browse renders is deliberate: a badge computed independently
 * would eventually disagree with the list it points at.
 */

/**
 * Does this application fall inside the funder's saved Settings filters?
 *
 * An application with no state on file is kept rather than dropped — that is
 * missing data, not a mismatch, and hiding it would bury work silently.
 */
export const passesFunderSettings = (app, settings) => {
  const { max_loan_amount: maxAmount, preferred_states: states } = settings || {};
  if (maxAmount && parseFloat(app.requestedAmount || 0) > maxAmount) return false;
  if (states && states.length > 0) {
    const appState = (app.accidentState || app.state || '').toUpperCase();
    if (appState && !states.includes(appState)) return false;
  }
  return true;
};

/** How many of these applications are still waiting for an offer from this funder. */
export const countActionable = (apps, settings) =>
  (apps || []).filter(app =>
    !app.funder_has_offer &&
    !app.funder_not_interested &&
    passesFunderSettings(app, settings)
  ).length;

/**
 * Offers a funder can still act on. Mirrors LIVE_OFFER_STATUSES on the server
 * (backend/funder_offers.py) and in FunderOfferBuilder.
 *
 * `funder_has_offer` is not the same question: it stays true for a withdrawn or
 * declined offer, which are history and do not block a new one. Keying an
 * action off it alone dead-ends a funder who revoked an offer and wants to bid
 * again — the exact flow revoke-then-offer-again exists for.
 */
export const LIVE_OFFER_STATUSES = ['draft', 'submitted'];

export const hasLiveOffer = (app) => LIVE_OFFER_STATUSES.includes(app.offer_status);

/**
 * Can this funder still win the application?
 *
 * All three clauses matter. Another funder must have bid, or there is nothing
 * to beat. The plaintiff must not have chosen — guaranteed here rather than
 * checked, because accepting an offer moves the application to `funded` and
 * Browse lists only `approved` ones. And THIS funder must have no live offer:
 * without that, a card saying "Make an offer" sat above the same application's
 * row saying "Update offer".
 */
export const isWinnable = (app, settings) =>
  !!app.has_other_offers &&
  !hasLiveOffer(app) &&
  !app.funder_not_interested &&
  passesFunderSettings(app, settings);
